import { useState } from "react";
import { Article, ChatText, PencilSimple, PhoneCall } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, formatApiError } from "../../lib/api";
import { formatPhone, telHref } from "../../lib/phone";
import { StatusPill } from "../lux/Primitives";
import { LEAD_STATUS_TONE } from "./leadActions";
import { WebsiteRequestBlock } from "./WebsiteRequestBlock";

// LEAD-001: a read-only look at one lead. "View details" on every row, card and
// My Leads row opens it; it fetches the full lead (the same call the Edit form
// makes), so the list itself stays lean. Nothing here can be changed: "Edit lead"
// hands over to the existing edit form.

const when = (s) => {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(s);
  const date = d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  const hasTime = /T\d{2}:\d{2}/.test(String(s));
  return hasTime ? `${date} · ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }).toLowerCase()}` : date;
};
const money = (n) => (Number(n) > 0 ? `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "");
const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : "");

function Section({ title, rows }) {
  const shown = rows.filter(([, v]) => v !== "" && v !== null && v !== undefined && v !== false);
  if (!shown.length) return null;
  return (
    <section>
      <div className="label-mono mb-2">{title}</div>
      <dl className="grid grid-cols-2 gap-x-4 sm:gap-x-6 gap-y-2.5 text-sm">
        {shown.map(([k, v, wide]) => (
          <div key={k} className={`min-w-0 ${wide ? "col-span-2" : ""}`}>
            <dt className="text-xs text-muted-foreground">{k}</dt>
            <dd className="mt-0.5 break-words">{v}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export function LeadDetailsButton({ lead, onEdit, testid, className = "" }) {
  const [open, setOpen] = useState(false);
  const [full, setFull] = useState(null);
  const [error, setError] = useState("");

  const show = async (e) => {
    e.stopPropagation();
    setOpen(true); setFull(null); setError("");
    try { const { data } = await api.get(`/leads/${lead.id}`); setFull(data); }
    catch (err) { setError(formatApiError(err)); }
  };

  const l = full || lead;
  const message = String(l.notes || "").trim();
  const website = l.website_details
    ? { ...l.website_details, customer_notes: String(l.website_details.customer_notes || "").trim() === message ? "" : l.website_details.customer_notes }
    : null;
  const phone = l.customer_phone && telHref(l.customer_phone)
    ? <a href={telHref(l.customer_phone)} className="inline-flex items-center gap-1.5 text-primary hover:underline"><PhoneCall size={14} />{formatPhone(l.customer_phone)}</a>
    : formatPhone(l.customer_phone);
  const email = l.customer_email ? <a href={`mailto:${l.customer_email}`} className="text-primary hover:underline">{l.customer_email}</a> : "";
  const status = (l.status || "new").toLowerCase();
  const sub = [l.lead_no ? `#${l.lead_no}` : "", cap(l.service_type || "rental"), l.source].filter(Boolean).join(" · ");

  return (
    <>
      <button
        type="button"
        onClick={show}
        className={`inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors ${className}`}
        data-testid={testid}
      >
        <Article size={14} aria-hidden="true" />View details
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100%-1.5rem)] max-w-xl p-5 sm:p-6 bg-card border-border max-h-[90vh] overflow-y-auto" data-testid={testid ? `${testid}-dialog` : undefined}>
          <DialogHeader className="text-left">
            <div className="flex items-start justify-between gap-3 pr-6">
              <div className="min-w-0">
                <DialogTitle className="font-heading text-2xl truncate">{l.customer_name || "Unnamed lead"}</DialogTitle>
                <DialogDescription className="mt-1">{sub}</DialogDescription>
              </div>
              <StatusPill status={status} tone={LEAD_STATUS_TONE[status]} className="shrink-0 mt-1" />
            </div>
          </DialogHeader>

          {error && <p className="text-sm text-destructive">{error}</p>}
          {!full && !error && <p className="text-sm text-muted-foreground">Loading…</p>}

          {full && (
            <div className="space-y-5">
              {message && (
                <section className="rounded-xl border border-border bg-background/60 p-4" data-testid={testid ? `${testid}-message` : undefined}>
                  <div className="label-mono mb-2 flex items-center gap-1.5"><ChatText size={13} />Message</div>
                  <p className="text-sm leading-relaxed whitespace-pre-line break-words">{message}</p>
                </section>
              )}
              <Section title="Trip" rows={[
                ["Service", cap(l.service_type || "rental")],
                ["Vehicle", l.vehicle_preference],
                ["Pickup", when(l.pickup_date)],
                ["Return", when(l.return_date)],
                ["Pickup location", l.pickup_location, true],
                ["Drop-off location", l.dropoff_location, true],
              ]} />
              <Section title="Contact" rows={[["Phone", phone], ["Email", email, true]]} />
              <Section title="Lead" rows={[
                ["Came in", when(l.received_at || l.created_at)],
                ["Source", l.source],
                ["Assigned to", l.salesperson || "Unassigned"],
                ["Quoted", money(l.quoted_amount)],
                ["Next follow-up", when(l.next_follow_up_at)],
              ]} />
              {website && <div className="grid grid-cols-2"><WebsiteRequestBlock details={website} /></div>}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid={testid ? `${testid}-close` : undefined}>Close</Button>
            {onEdit && (
              <Button onClick={() => { setOpen(false); onEdit(lead); }} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid={testid ? `${testid}-edit` : undefined}>
                <PencilSimple size={14} className="mr-1.5" />Edit lead
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
